import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'reflexbox';

import { useLocalize, useViewerRoles } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import { Select } from '@treams/treams-ui';

function containsJobTitle(template: graphql.FeedbackTemplate, jobTitleIDs: string[]) {
	// Templates without job title IDs are available to everyone.
	if (!template.jobTitleIDs || template.jobTitleIDs.length === 0) {
		return true;
	}

	// Otherwise the template is only available when it shares a job title with the user.
	return template.jobTitleIDs.some((jobTitleID) => jobTitleIDs.includes(jobTitleID));
}

export default function FeedbackTemplateSelector(props: {
	template?: graphql.FeedbackTemplate;
	onChange: (template?: graphql.FeedbackTemplate) => void;
	showPrivate?: boolean;
	method: 'template' | 'custom' | 'competencies' | 'goals';
	userID?: string;
	disabled?: boolean;
}) {
	const { t } = useTranslation();
	const { localize } = useLocalize();
	const { isWorkspaceSuperuser } = useViewerRoles();
	const query = graphql.useFeedbackTemplateSelectorQuery({
		variables: {
			filters: {
				role:
					props.method === 'competencies'
						? graphql.FeedbackTemplateRole.GrowthPath
						: graphql.FeedbackTemplateRole.Request,
			},
		},
	});

	const jobPositionsQuery = graphql.useJobPositionsQuery({
		variables: { id: props.userID!, status: graphql.JobPositionStatus.Active },
		skip: !props.userID,
	});

	const userJobTitleIDs = jobPositionsQuery.data?.user.jobPositions.map(
		(jobPosition) => jobPosition.jobTitle.id,
	);

	let options: { title: string; value: string; data: graphql.FeedbackTemplate }[] =
		query.data?.feedbackTemplates.edges.map(({ node }) => ({
			title: localize(node.title).text,
			value: node.id,
			data: node as graphql.FeedbackTemplate,
		})) ?? [];

	if (!props.showPrivate) {
		options = options.filter((option) => !option.data.isPrivate);
	}

	// Filter by the feedback subject's job titles. Superusers always see all templates,
	// and when no subject is provided we cannot filter so all templates remain visible.
	if (!isWorkspaceSuperuser && props.userID) {
		options = options.filter((option) => containsJobTitle(option.data, userJobTitleIDs ?? []));
	}

	// For competencies, if there is only one option, select it automatically
	useEffect(() => {
		if (props.disabled) {
			return;
		}

		if (props.method !== 'competencies') {
			return;
		}

		if (options.length === 1) {
			props.onChange(options[0].data);
		}
	}, [options, props.onChange]);

	// For competencies, if there are no options, hide the template selector
	if (options.length === 0 && props.method === 'competencies') {
		return null;
	}

	return (
		<Box width="100%">
			<Select
				search
				disabled={props.disabled}
				placeholder={
					props.disabled
						? t('common.feedback.choosenTemplate')
						: t('common.feedback.selectTemplate')
				}
				options={options}
				selectedValue={options.find((option) => option.value === props.template?.id)?.value}
				onChange={(value) => {
					props.onChange(options.find((option) => option.value === value)?.data);
				}}
			/>
		</Box>
	);
}
